import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, fns } from "../firebase";
import { useSession } from "../auth.jsx";
import { criteriaFor, computeMark, SCALE, STRANDS, GATES } from "../lib/rubric.js";
import { Eyebrow, TierStamp, CheckList } from "../components/Bits.jsx";

export default function Console() {
  const { subId } = useParams();
  const nav = useNavigate();
  const s = useSession();

  const [sub, setSub] = useState(null);
  const [brief, setBrief] = useState(null);
  const [draft, setDraft] = useState(null);
  const [scores, setScores] = useState({});
  const [keep, setKeep] = useState("");
  const [changes, setChanges] = useState([{ change: "", principle: "" }, { change: "", principle: "" }]);
  const [gate, setGate] = useState({ status: "", reason: "" });
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => onSnapshot(doc(db, "submissions", subId), async (d) => {
    if (!d.exists()) return;
    const data = { id: d.id, ...d.data() };
    setSub(data);
    const b = await getDoc(doc(db, "briefs", data.briefId));
    if (b.exists()) setBrief({ id: b.id, ...b.data() });
  }), [subId]);

  useEffect(() => onSnapshot(doc(db, "drafts", subId), (d) => setDraft(d.exists() ? d.data() : null)), [subId]);

  const criteria = useMemo(() => (sub ? criteriaFor(sub.briefId) : []), [sub]);
  const result = useMemo(() => (sub ? computeMark(sub.briefId, scores) : null), [sub, scores]);
  const gateId = useMemo(
    () => Object.entries(GATES).find(([, g]) => g.brief === sub?.briefId)?.[0] ?? null,
    [sub]
  );

  const claim = async () => {
    await updateDoc(doc(db, "submissions", subId), { state: "in_review", assignedTo: s.user.uid });
  };

  const runDraft = async () => {
    setBusy("draft"); setMsg(null);
    try {
      const call = httpsCallable(fns, "draftScores");
      await call({ subId, criteria: criteria.map((c) => ({ id: c.id, label: c.label, humanOnly: c.humanOnly, anchor3: c.anchor3 })) });
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally { setBusy(""); }
  };

  const release = async () => {
    setBusy("release"); setMsg(null);
    try {
      const call = httpsCallable(fns, "releaseMark");
      const res = await call({
        subId,
        scores,
        feedback: { keep, changes },
        gateOutcome: gateId && gate.status ? { gateId, status: gate.status, reason: gate.reason } : null,
      });
      setMsg({
        kind: "ok",
        text: res.data.auditSelected
          ? "Released. This mark was drawn into the 20% audit sample — your Studio Director will re-read it."
          : "Released to the resident.",
      });
      setTimeout(() => nav("/queue"), 1600);
    } catch (e) {
      setMsg({ kind: "error", text: e.message });
    } finally { setBusy(""); }
  };

  if (!sub) return <p style={{ color: "var(--graphite)" }}>Loading submission…</p>;

  const draftFor = (id) => (draft?.drafts ?? []).find((d) => d.criterionId === id);
  const feedbackReady = keep.trim() && changes.filter((c) => c.change.trim() && c.principle.trim()).length >= 2;
  const canRelease = result?.complete && feedbackReady && !busy;

  return (
    <div>
      <button className="btn ghost" onClick={() => nav("/queue")} style={{ marginBottom: 14 }}>← Queue</button>

      <div className="row between wrapflex" style={{ marginBottom: 6 }}>
        <div>
          <Eyebrow>{brief?.month} · {brief?.block}</Eyebrow>
          <h1 style={{ fontSize: 26, marginTop: 4 }}>
            <span className="mono" style={{ fontSize: 17, color: "var(--graphite)", marginRight: 10 }}>{sub.briefId}</span>
            {brief?.title ?? "—"}
          </h1>
          <p style={{ color: "var(--graphite)", fontSize: 13 }}>
            {sub.residentName ?? sub.residentUid} · pod {sub.podId ?? "—"} · submitted{" "}
            {sub.submittedAt?.toDate?.().toLocaleString?.() ?? "—"}
          </p>
        </div>
        <TierStamp tier={sub.tier ?? brief?.tier} />
      </div>

      {sub.state === "queued" && (
        <button className="btn" onClick={claim} style={{ marginBottom: 14 }}>Claim for review</button>
      )}

      <div className="grid2">
        {/* -------- left: the evidence -------- */}
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <Eyebrow>The charge</Eyebrow>
            <p style={{ marginTop: 6 }}>{brief?.charge}</p>
            <Eyebrow style={{ marginTop: 12 }}>AI prohibited on this brief</Eyebrow>
            <p style={{ marginTop: 4, fontSize: 14 }}>{brief?.prohibited}</p>
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <Eyebrow>L1 · deterministic checks</Eyebrow>
            <div style={{ marginTop: 8 }}><CheckList l1={sub.l1} /></div>
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <Eyebrow>Artefacts</Eyebrow>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {(sub.artefactUrls ?? []).map((u, i) => (
                <li key={i}><a href={u} target="_blank" rel="noreferrer">{u}</a></li>
              ))}
              {sub.repoUrl && <li><a href={sub.repoUrl} target="_blank" rel="noreferrer">Repository</a></li>}
              {!sub.artefactUrls?.length && !sub.repoUrl && <li style={{ color: "var(--graphite)" }}>None attached.</li>}
            </ul>
          </div>

          <div className="card">
            <Eyebrow>Provenance ledger</Eyebrow>
            <div className="warn" style={{ margin: "10px 0" }}>
              <Eyebrow style={{ color: "var(--madder)" }}>Read this first</Eyebrow>
              <p style={{ marginTop: 6, fontSize: 14 }}>
                {sub.ledger?.wrong?.trim() || <em style={{ color: "var(--madder)" }}>Blank. A resident who cannot name a machine error has not been verifying — score Verification accordingly.</em>}
              </p>
            </div>
            <table>
              <thead><tr><th>Element</th><th>Human</th><th>Machine</th><th>Verified</th></tr></thead>
              <tbody>
                {(sub.ledger?.rows ?? []).map((r, i) => (
                  <tr key={i}><td>{r.element}</td><td>{r.human}</td><td>{r.machine}</td><td>{r.verification}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="mono" style={{ fontSize: 12, marginTop: 8, color: "var(--graphite)" }}>
              models: {sub.ledger?.models || "—"} · cycles: {sub.ledger?.cycles || "—"} ·{" "}
              {sub.ledger?.signed ? <span className="pass">signed</span> : <span className="fail">UNSIGNED — not markable</span>}
            </p>
          </div>
        </div>

        {/* -------- right: the mark -------- */}
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="row between">
              <Eyebrow>L2 · draft, staff only</Eyebrow>
              <button className="btn ghost" onClick={runDraft} disabled={busy === "draft"}>
                {busy === "draft" ? "Drafting…" : draft ? "Re-draft" : "Request draft"}
              </button>
            </div>
            <p style={{ fontSize: 13, color: "var(--graphite)", marginTop: 6 }}>
              Drafts are advisory, never released, and never produced for Judgement criteria.
              {draft?.discarded ? ` ${draft.discarded} uncited score(s) were discarded.` : ""}
            </p>
            {draft?.flags?.length > 0 && (
              <div className="warn" style={{ marginTop: 10 }}>
                <Eyebrow style={{ color: "var(--madder)" }}>Flags</Eyebrow>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {draft.flags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div className="card">
            <Eyebrow>Your mark</Eyebrow>
            {criteria.map((c) => {
              const d = draftFor(c.id);
              return (
                <div className="crit" key={c.id}>
                  <div className="row between wrapflex">
                    <div>
                      <strong>{c.label}</strong>{" "}
                      <span className="pill">{STRANDS[c.strand].label}</span>{" "}
                      {c.humanOnly && <span className="pill" style={{ color: "var(--madder)" }}>human only</span>}
                    </div>
                    <div className="scale">
                      {SCALE.map((sc) => (
                        <button
                          key={sc.v}
                          title={sc.label}
                          aria-pressed={scores[c.id] === sc.v}
                          onClick={() => setScores({ ...scores, [c.id]: sc.v })}
                        >{sc.v}</button>
                      ))}
                    </div>
                  </div>
                  {c.anchor3 && <p style={{ fontSize: 12, color: "var(--graphite)", margin: "4px 0 0" }}>3 = {c.anchor3}</p>}
                  {d && (
                    <div className="draftbox" style={{ marginTop: 6 }}>
                      <strong>Draft {d.score}</strong> — {d.evidence}
                      {scores[c.id] && scores[c.id] !== d.score && (
                        <span className="mono" style={{ marginLeft: 8, color: "var(--madder)" }}>
                          you: {scores[c.id]} ({scores[c.id] - d.score > 0 ? "+" : ""}{scores[c.id] - d.score})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="row wrapflex" style={{ marginTop: 12, gap: 16 }}>
              <div><Eyebrow>Overall</Eyebrow><div className="mono" style={{ fontSize: 22 }}>{result.overall}</div></div>
              {Object.entries(result.strandPct).map(([k, v]) => (
                <div key={k}>
                  <Eyebrow>{STRANDS[k].label}</Eyebrow>
                  <div className={`mono ${v < STRANDS[k].floor ? "fail" : ""}`} style={{ fontSize: 18 }}>{v}</div>
                </div>
              ))}
            </div>
            {result.belowFloor.length > 0 && (
              <p className="fail" style={{ fontSize: 13 }}>
                Below the 70 floor: {result.belowFloor.map((k) => STRANDS[k].label).join(", ")}. Say so explicitly in the feedback.
              </p>
            )}
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <Eyebrow>Feedback · one keep, two changes, a principle each</Eyebrow>
            <p style={{ fontSize: 12, color: "var(--graphite)", margin: "4px 0 8px" }}>
              Praise without a reason is prohibited in faculty feedback exactly as it is in peer critique.
            </p>
            <label className="eyebrow">Keep — and why it works</label>
            <textarea rows={2} value={keep} onChange={(e) => setKeep(e.target.value)} />
            {changes.map((c, i) => (
              <div key={i} style={{ marginTop: 10 }}>
                <label className="eyebrow">Change {i + 1}</label>
                <input type="text" value={c.change}
                  onChange={(e) => setChanges(changes.map((x, j) => j === i ? { ...x, change: e.target.value } : x))} />
                <input type="text" placeholder="The principle or evidence this rests on" value={c.principle}
                  style={{ marginTop: 4 }}
                  onChange={(e) => setChanges(changes.map((x, j) => j === i ? { ...x, principle: e.target.value } : x))} />
              </div>
            ))}
            <button className="btn ghost" style={{ marginTop: 10 }}
              onClick={() => setChanges([...changes, { change: "", principle: "" }])}>+ change</button>
          </div>

          {gateId && (
            <div className="card" style={{ marginTop: 14, borderColor: "var(--madder)" }}>
              <Eyebrow style={{ color: "var(--madder)" }}>Gate {gateId} · {GATES[gateId].name}</Eyebrow>
              {s.isSenior ? (
                <>
                  <div className="row" style={{ marginTop: 8 }}>
                    {["passed", "failed"].map((st) => (
                      <button key={st} className={`btn ${gate.status === st ? "" : "ghost"}`}
                        onClick={() => setGate({ ...gate, status: st })}>{st}</button>
                    ))}
                  </div>
                  <input type="text" placeholder="Reason — the resident sees this" style={{ marginTop: 8 }}
                    value={gate.reason} onChange={(e) => setGate({ ...gate, reason: e.target.value })} />
                  <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 6 }}>
                    Resubmissions permitted: {GATES[gateId].resubmissions ?? "unlimited"}. A gate waived once is not a gate in the next cohort.
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 13, marginTop: 6 }}>
                  This brief carries a gate. Record your recommendation in the feedback; only a Studio Director or the
                  Programme Lead adjudicates it.
                </p>
              )}
            </div>
          )}

          {msg && <div className={msg.kind === "ok" ? "card" : "warn"} style={{ marginTop: 14 }}>{msg.text}</div>}

          <button className="btn" style={{ marginTop: 14, width: "100%" }} disabled={!canRelease} onClick={release}>
            {busy === "release" ? "Releasing…" : "Release mark to resident"}
          </button>
          {!canRelease && (
            <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 6 }}>
              {!result.complete && `Score all ${result.criteriaCount} criteria. `}
              {!feedbackReady && "Feedback needs one keep and two changes with principles."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
